
import { Component, Vue } from 'vue-property-decorator';
import { checkRole } from '@/modules/authentication/helpers/checkRole';
import { Roles } from '@/modules/authentication/models/rolesEnum';
import {
  convertFactSampleArrayToStepSampleArray,
  isValidSampleStatusWorkflow,
  convertStepSampleArrayToFactSampleArray,
  updateStepSampleArray
} from '@/modules/analysis/helpers/analysisSamplesWorkflow';
import { FactSample, Sample, SampleBatch, StepSample } from '@/modules/analysis/models/analysisRequest';
import * as SampleService from '@/modules/analysis/services/sampleService';
import * as SampleBatchService from '@/modules/analysis/services/sampleBatchService';
import { RequestStatusEnum } from '@/modules/analysis/models/requestStatusEnum';

@Component({
  components: {}
})
export default class SampleBatchStatus extends Vue {
  protected sampleBatch: SampleBatch | null = null;
  protected inputSamples: Sample[] = [];
  protected loadingCard = false;
  protected sendingRequest = false;
  protected requestError = false;
  protected isNewCheckStatus = false;

  protected async created(): Promise<void> {
    this.loadingCard = true;
    await SampleBatchService.read(this.$route.params.id)
      .then((response) => {
        this.sampleBatch = response.data;
        this.loadFactSamples();
      })
      .catch((e) => {
        if (e.response.status === 404) {
          this.$router.push({ name: '404' });
        }
      })
      .finally(() => (this.loadingCard = false));
  }

  protected async loadFactSamples(): Promise<void> {
    if (this.sampleBatch?.samples) {
      const samples: Sample[] = [];
      await SampleBatchService.findFactSamplesBySampleBatchId(this.sampleBatch.id as string).then((response) => {
        for (const sample of response.data) {
          const steps = convertFactSampleArrayToStepSampleArray(sample.facts as FactSample[]);
          const inputSample: Sample = {
            id: sample.id,
            reference: sample.reference,
            origin: sample.origin,
            size: sample.size,
            sizeUnit: sample.sizeUnit,
            samplingDate: sample.samplingDate,
            steps: steps
          };
          samples.push(inputSample);
        }
      });
      this.inputSamples = samples;
    }
  }

  protected validate(): void {
    this.sendingRequest = true;
    const samplesSend: SampleService.SampleBatchStatusRequest = { status: [] };
    setTimeout(async () => {
      if (this.inputSamples) {
        this.isNewCheckStatus = false;
        for (const sample of this.inputSamples) {
          const inputFacts: FactSample[] = convertStepSampleArrayToFactSampleArray(sample.steps as StepSample[][]);
          if (inputFacts.length > 0) {
            this.isNewCheckStatus = true;
            samplesSend.status.push({
              id: sample.id as string,
              facts: inputFacts
            });
          }
        }
        await this.updateSampleStatus(samplesSend);
        if (!this.isNewCheckStatus) {
          this.sendingRequest = false;
        }
      }
    }, 500);
  }
  protected async updateSampleStatus(samplesSend: SampleService.SampleBatchStatusRequest): Promise<void> {
    await SampleService.updateStatus(samplesSend)
      .then(() => {
        for (const sample of samplesSend.status) {
          const steps = this.inputSamples.find((item) => item.id === sample.id)?.steps;
          if (steps) {
            for (const fact of sample.facts as FactSample[]) {
              const stepSaved: StepSample = { status: fact.status, value: true, isNew: false };
              updateStepSampleArray(steps as StepSample[][], stepSaved);
            }
          }
        }
        this.requestError = false;
      })
      .catch(() => {
        this.requestError = true;
      })
      .finally(() => {
        this.sendingRequest = false;
        this.isNewCheckStatus = false;
      });
  }

  protected checkAll(status: string): void {
    const elements = this.$refs[`${status}`] as HTMLFormElement[];
    elements.forEach((element) => {
      element.$refs.input.click();
    });
  }

  protected isValidSampleStatusWorkflow(steps: StepSample[][], step: StepSample): boolean {
    const result = isValidSampleStatusWorkflow(steps, step.status);
    if (!result) {
      step.value = false; // unchecked boxe when status workflow is invalid
    }
    return result;
  }

  protected hasStatusSelected(): boolean {
    for (const sample of this.inputSamples) {
      for (const step of sample.steps || []) {
        for (const status of step) {
          if (status.value === true && status.isNew === true) {
            return true;
          }
        }
      }
    }
    return false;
  }

  protected get headers(): string[] {
    const labels: string[] = [];
    const steps = this.inputSamples[0]?.steps || [];
    if (steps) {
      for (const step of steps) {
        for (const sampleStatus of step) {
          labels.push(sampleStatus.status);
        }
      }
    }
    return labels;
  }

  protected get isSamplesStateUpdateAuthorized(): boolean {
    return checkRole(Roles.samples_update_state);
  }

  protected get isReadOnly(): boolean {
    return this.sampleBatch?.analysisCurrentStatus !== RequestStatusEnum.IN_PROGRESS;
  }
}
